import { Box, FormHelperText, InputLabel, TextField, TextFieldProps, styled, useTheme } from '@mui/material';
import { FormattedNumber } from 'react-intl';
import { ReactElement, ReactNode } from 'react';

export interface PoolTextfieldPropsOwnProps {
    hint?: ReactNode;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
    readonly?: boolean;
    textAlign?: 'left' | 'center' | 'right';
}

const StyledTextfield = styled(TextField)(() => ({
    minWidth: 90,
}));

export type PoolTextfieldProps = PoolTextfieldPropsOwnProps & TextFieldProps;

/**
 * PoolTextfield<br>
 * <a href="https://www.figma.com/file/kjfAT06qgLdW52cF1SqWuy/Pool-Alpin-Shop-2.0-Library?node-id=721%3A10055" target="_blank">Open in Figma</a>
 */
export const PoolTextfield = (props: PoolTextfieldProps): ReactElement => {
    const { readonly, hint, startAdornment, endAdornment, ...textFieldProps } = props;

    const myInputPropsObject = {
        startAdornment,
        endAdornment,
        textAlign: props.textAlign,
        ...props.InputProps,
    };

    const helperText = props.helperText && props.error ? props.helperText : hint || '';

    return readonly ? (
        <PoolReadonlyTextField {...textFieldProps} InputProps={myInputPropsObject} helperText={helperText} />
    ) : (
        <StyledTextfield {...textFieldProps} InputProps={myInputPropsObject} helperText={helperText} />
    );
};

const PoolReadonlyTextField = (props: TextFieldProps): ReactElement => {
    const theme = useTheme();
    const currency = props.InputProps?.inputProps?.['currency'];
    const currencySymbol = props.InputProps?.inputProps?.['currencySymbol'];
    const suffix = props.InputProps?.inputProps?.['suffix'];
    // A number field read only printed its raw value: "100000 Stück", "1.49".
    const numberInput = props.InputProps?.inputProps;
    const grouped =
        !!numberInput &&
        'hasThousandSeparator' in numberInput &&
        false !== numberInput['hasThousandSeparator'] &&
        '' !== props.value &&
        null !== props.value &&
        !isNaN(Number(props.value));
    // Without label and message — a figure in a table row — nothing is held
    // free above or below it. The room for both made a read-only position row
    // 57 instead of 38 px high (#2250).
    const bare = !props.label && !props.helperText;

    return (
        <Box
            className={`MuiFormControl-root MuiTextField-root ${props.fullWidth ? 'MuiFormControl-fullWidth' : ''}`}
            sx={{ minWidth: 90, mt: bare ? 0 : 2 }}
        >
            {!bare && (
                <InputLabel required={props.required} shrink={true} error={props.error} disabled={props.disabled}>
                    {props.label}
                </InputLabel>
            )}
            <Box
                sx={{
                    ml: bare ? 0 : 2,
                    mt: bare ? 0 : -0.5,
                    ...(props.disabled && { color: theme.palette.text.disabled }),
                }}
            >
                {grouped && !currency ? (
                    <>
                        <FormattedNumber
                            value={Number(props.value)}
                            minimumFractionDigits={numberInput['fixedDecimalScale'] ? numberInput['decimalScale'] : 0}
                            maximumFractionDigits={numberInput['decimalScale'] ?? 20}
                        />
                        {suffix}
                    </>
                ) : isNaN(Number(props.value)) || !currency ? (
                    <>
                        {props.value as string}
                        {suffix}
                    </>
                ) : false === currencySymbol ? (
                    // The amount keeps its two decimals; the sign stands in
                    // the column heading instead (LST-S17).
                    <FormattedNumber
                        value={props.value as number}
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                    />
                ) : (
                    <FormattedNumber value={props.value as number} style="currency" currency={currency} />
                )}
            </Box>
            {!bare && (
                <FormHelperText error={props.error} disabled={props.disabled} sx={{ mt: '19px', ml: 2 }}>
                    {props.helperText}
                </FormHelperText>
            )}
        </Box>
    );
};
